import { Help, Input, authenticationService } from '@enkla-gui'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { icons } from '@config'
import { useForm } from 'react-hook-form'

const MFA_STORAGE_KEY = 'mfa_challenge'

function readChallenge(location) {
  if (location.mfa_token) {
    return { mfa_token: location.mfa_token, from: location.from }
  }
  if (location.state?.mfa_token) {
    return { mfa_token: location.state.mfa_token, from: location.state.from }
  }
  try {
    const stored = sessionStorage.getItem(MFA_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

export default function MfaChallenge() {
  const { register, handleSubmit, setValue } = useForm({
    defaultValues: { remember_device: true }
  })
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [useRecovery, setUseRecovery] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [mfaToken, setMfaToken] = useState(null)
  const [from, setFrom] = useState({ pathname: '/dashboard' })
  const [ready, setReady] = useState(false)
  const location = useLocation()
  const history = useHistory()

  useEffect(() => {
    if (authenticationService.currentTokenValue !== null) {
      history.replace('/dashboard')
      return
    }

    const challenge = readChallenge(location)
    if (!challenge?.mfa_token) {
      history.replace('/login')
      return
    }

    setMfaToken(challenge.mfa_token)
    if (challenge.from) setFrom(challenge.from)
    setReady(true)
  }, [history, location])

  const clearChallenge = () => {
    try {
      sessionStorage.removeItem(MFA_STORAGE_KEY)
    } catch {
      // ignore
    }
  }

  const onSubmit = data => {
    if (!mfaToken) return
    setLoading(true)
    setError(null)
    authenticationService
      .verifyMfa({
        mfa_token: mfaToken,
        code: data.code,
        remember_device: !!data.remember_device
      })
      .then(() => {
        clearChallenge()
        history.push(from)
      })
      .catch(result => {
        try {
          const [, res] = result
          if (res?.mfa_token) {
            setMfaToken(res.mfa_token)
            try {
              sessionStorage.setItem(
                MFA_STORAGE_KEY,
                JSON.stringify({ mfa_token: res.mfa_token, from })
              )
            } catch {
              // ignore
            }
          } else {
            clearChallenge()
            history.replace({
              pathname: '/login',
              message: res?.message || 'Verifieringen har gått ut. Logga in igen.'
            })
            return
          }
          setError(res)
          setValue('code', '')
        } catch {
          setError({ message: 'Något gick fel, försök igen' })
        }
      })
      .finally(() => setLoading(false))
  }

  if (!ready) return null

  return (
    <div className='full-height login-page'>
      <div className='login-card'>
        <div className='login-card-inner'>
          <div className='login-info'>
            <div className='login-info-content'>
              <span className='login-welcome'>Ett steg till</span>
              <h2>Verifieringskod</h2>
              <p className='login-info-subtitle'>
                Öppna din autentiseringsapp (t.ex. Google Authenticator) och ange den 6-siffriga koden.
              </p>
              <div className='login-accent-line' />
            </div>
          </div>
          <div className='login-form-section'>
            <form onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <Help header={false} type='warning' icon={icons.warning}>
                  <span>{error.message}</span>
                </Help>
              )}
              <Input
                controls={{ register }}
                name='code'
                label={useRecovery ? 'Återställningskod' : 'Verifieringskod'}
                type='text'
                placeholder={useRecovery ? 'XXXX-XXXX' : '000000'}
                options={{ required: true }}
              />
              <div className='form-check mb-3'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='remember_device'
                  {...register('remember_device')}
                />
                <label className='form-check-label' htmlFor='remember_device'>
                  Kom ihåg den här enheten i 30 dagar
                </label>
              </div>
              <button disabled={loading} type='submit' className='btn login-btn'>
                {loading ? (
                  <>
                    <i className='fas fa-spinner fa-spin' /> Verifierar...
                  </>
                ) : (
                  'Verifiera och logga in'
                )}
              </button>
            </form>
            <div className='login-card-links'>
              <p className='login-forgot-link'>
                <a
                  href='#/'
                  onClick={e => {
                    e.preventDefault()
                    setUseRecovery(!useRecovery)
                    setValue('code', '')
                    setError(null)
                  }}>
                  {useRecovery ? 'Använd autentiseringsapp i stället' : 'Använd återställningskod'}
                </a>
              </p>
              <p className='login-forgot-link'>
                <a
                  href='#/'
                  onClick={e => {
                    e.preventDefault()
                    setShowHelp(!showHelp)
                  }}>
                  Har du tappat din telefon?
                </a>
              </p>
              {showHelp && (
                <Help header={false} type='info' icon={icons.info}>
                  <ol className='mb-0 ps-3'>
                    <li>Använd en återställningskod om du sparade dem när du aktiverade MFA.</li>
                    <li>
                      Be en kontoadministratör i ditt företag återställa MFA under Inställningar → Användare.
                    </li>
                    <li>Saknas det? Kontakta vår support på kontakt@enklafastigheter.se.</li>
                  </ol>
                </Help>
              )}
              <p className='login-forgot-link'>
                <a
                  href='#/login'
                  onClick={() => clearChallenge()}>
                  Tillbaka till inloggning
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
